import {
  MAX_SKILL_RESOURCE_PATH_BYTES,
  MAX_SKILL_RESOURCE_PATH_SEGMENTS,
} from "../constants/skills";
import type {
  IFindShadowedResourcePathProps,
  IValidateSkillResourceFolderProps,
  TValidateSkillResourceFolderResult,
} from "./skill-resource-path.types";
import { ESkillResourceFolderRejection } from "./skill-resource-path.types";

// NOTE: NSAI-5804 — a skill keeps the folder structure it was imported with
// (`references/glossary.md`, `assets/note.txt`), but "Add files" had no way to
// name a destination: every upload stored `file.name` and therefore landed at the
// bundle root. Because the duplicate check is on the FULL path, uploading
// `glossary.md` to a skill that already holds `references/glossary.md` was
// accepted — creating a second file the SKILL.md never references, which looks to
// the person like the update worked. Re-importing the whole ZIP was the only way
// to change anything inside a folder.
//
// This is the client-side mirror of the server's `validateSkillResourcePath`, and
// it is deliberately no more permissive: every rule below has a counterpart there
// (which stays the authority, since it re-checks the object at rest). It exists so
// a refusal can name the folder and the reason before bytes are spent uploading.

const PATH_SEPARATOR = "/";
const CURRENT_DIRECTORY_SEGMENT = ".";
const PARENT_DIRECTORY_SEGMENT = "..";
const BACKSLASH = "\\";
const C0_CONTROL_RANGE_END = 0x1f;
const C1_CONTROL_RANGE_START = 0x7f;
const C1_CONTROL_RANGE_END = 0x9f;

// NOTE: `C:file.md` is absolute on Windows without a leading separator, so a
// drive prefix has to be rejected on its own.
const WINDOWS_DRIVE_PREFIX_PATTERN = /^[a-zA-Z]:/;

// NOTE: NSAI-6082 — mirrors the server: a path is listed verbatim inside the
// system-role skill block, so one holding `<` or `>` could spell a closing tag.
// Neither is a legal Windows filename character, so refusing them here costs no
// portable file, and the person hears it before the upload instead of after.
const ANGLE_BRACKET_PATTERN = /[<>]/;

// NOTE: Counted in UTF-8 bytes, not characters — the column the path lands in and
// every filesystem below it count bytes, so a multibyte path would otherwise pass
// a `length` check here and fail the insert. `TextEncoder` rather than `Buffer`
// because this library has to run under Metro as well as Vite.
const PATH_ENCODER = new TextEncoder();

const hasControlCharacter = (value: string): boolean => {
  for (const character of value) {
    // NOTE: Iterating a string yields whole code points, so `codePointAt(0)` is
    // always defined; the fallback stays inside the control range so an
    // impossible read fails closed rather than passing validation.
    const codePoint = character.codePointAt(0) ?? 0;

    if (
      codePoint <= C0_CONTROL_RANGE_END ||
      (codePoint >= C1_CONTROL_RANGE_START && codePoint <= C1_CONTROL_RANGE_END)
    ) {
      return true;
    }
  }

  return false;
};

// NOTE: A segment that differs from its trimmed form renders as a different name
// than it stores — a spoofing surface, and a source of paths differing only by
// invisible whitespace.
const isPaddedSegment = (segment: string): boolean =>
  segment !== segment.trim();

// NOTE: A trailing separator is the one piece of sloppiness that is repaired
// rather than refused: typing `references/` is what a person does, it cannot mean
// anything else, and the alternative is an error message about a character they
// did not think of as part of the name. Everything else — a leading separator, a
// doubled one, a traversal segment — is rejected, because each of those changes
// which file the path denotes.
const trimTrailingSeparator = (folder: string): string => {
  let end = folder.length;

  while (end > 0 && folder[end - 1] === PATH_SEPARATOR) {
    end -= 1;
  }

  return folder.slice(0, end);
};

export const validateSkillResourceFolder = ({
  folder,
  fileName,
}: IValidateSkillResourceFolderProps): TValidateSkillResourceFolderResult => {
  // NOTE: NFC before anything else, and on the joined path rather than on the
  // parts, matching the server. Two byte-different encodings of the same
  // characters must resolve to one stored path, or a decomposed "café" folder and
  // a composed one both pass and land as rows the unique index sees as distinct
  // while a filesystem does not.
  const trimmedFolder = trimTrailingSeparator(folder.trim());
  const skillRelativePath = (
    trimmedFolder.length === 0
      ? fileName
      : `${trimmedFolder}${PATH_SEPARATOR}${fileName}`
  ).normalize("NFC");

  if (
    skillRelativePath.length === 0 ||
    hasControlCharacter(skillRelativePath) ||
    ANGLE_BRACKET_PATTERN.test(skillRelativePath) ||
    skillRelativePath.includes(BACKSLASH) ||
    WINDOWS_DRIVE_PREFIX_PATTERN.test(skillRelativePath)
  ) {
    return {
      isValid: false,
      reason: ESkillResourceFolderRejection.INVALID,
    };
  }

  if (
    PATH_ENCODER.encode(skillRelativePath).length >
    MAX_SKILL_RESOURCE_PATH_BYTES
  ) {
    return {
      isValid: false,
      reason: ESkillResourceFolderRejection.TOO_LONG,
    };
  }

  const segments = skillRelativePath.split(PATH_SEPARATOR);

  if (segments.length > MAX_SKILL_RESOURCE_PATH_SEGMENTS) {
    return {
      isValid: false,
      reason: ESkillResourceFolderRejection.TOO_DEEP,
    };
  }

  // NOTE: One pass covers every separator anomaly. An empty segment is what a
  // leading "/" and a "//" run both produce; "." and ".." are the traversal
  // forms. Rejected rather than resolved — a resolved path can still escape the
  // bundle root, and the server refuses these outright too.
  for (const segment of segments) {
    if (
      segment.length === 0 ||
      segment === CURRENT_DIRECTORY_SEGMENT ||
      segment === PARENT_DIRECTORY_SEGMENT ||
      isPaddedSegment(segment)
    ) {
      return {
        isValid: false,
        reason: ESkillResourceFolderRejection.INVALID,
      };
    }
  }

  return { isValid: true, skillRelativePath };
};

// NOTE: NFC and lower case, matching the server's canonicalizer and the unique
// index it mirrors (`message_files_skill_version_path_unique` collates on
// `lower()`).
export const toComparablePath = (skillRelativePath: string): string =>
  skillRelativePath.normalize("NFC").toLowerCase();

const readBaseName = (skillRelativePath: string): string => {
  const separatorIndex = skillRelativePath.lastIndexOf(PATH_SEPARATOR);

  return separatorIndex === -1
    ? skillRelativePath
    : skillRelativePath.slice(separatorIndex + 1);
};

// NOTE: NSAI-5804 — the path of an existing resource that shares this upload's
// FILE NAME while living somewhere else in the bundle, or null when there is
// none. This is the case the duplicate check cannot see and the one the ticket is
// really about: `glossary.md` at the root beside `references/glossary.md` is two
// files with one name, only one of which the SKILL.md refers to. The person is
// warned rather than refused — the two paths are genuinely distinct and the
// server accepts both, so this is "did you mean the other one", not "no".
export const findShadowedResourcePath = ({
  existingPaths,
  skillRelativePath,
}: IFindShadowedResourcePathProps): string | null => {
  const comparablePath = toComparablePath(skillRelativePath);
  const comparableBaseName = toComparablePath(readBaseName(skillRelativePath));

  return (
    existingPaths.find(
      (existingPath) =>
        toComparablePath(existingPath) !== comparablePath &&
        toComparablePath(readBaseName(existingPath)) === comparableBaseName,
    ) ?? null
  );
};
